export const BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://api.binaffan.com/api'
    : 'http://localhost:8000/api'

// Public API endpoints (Kimlik doğrulama gerektirmeyen)
const PUBLIC_ENDPOINTS = {
  site: {
    settings: '/public/site-settings'
  },
  products: {
    list: '/public/products',
    category: '/public/products/category',
    details: (productUrl) => `/product-details/${productUrl}`,
    getCategoryProducts: (categoryId) =>
      `/public/products/category/${categoryId}`,
    variations: (productId) => `/public/products/${productId}/variations`,
    attributes: (productId) => `/public/products/${productId}/attributes`,
    images: (productId) => `/public/products/${productId}/images`,
    metas: (productId, metaType) =>
      `/public/products/${productId}/metas/${metaType}`,
    specifications: (productId) =>
      `/public/products/${productId}/specifications`,
    buybox: () => `/public/products/buybox`,
    liveSearch: (query) => `/public/live-search?query=${encodeURIComponent(query)}`,
    colorTermsWithImages: (productId) =>
      `/public/products/${productId}/color-terms-with-images`
  },

  vendors: {
    getBySlug: (slug) => `/public/vendors/${slug}`,
    application: {
      submit: '/vendor/application',
      current: '/vendor/application/current',
      status: (applicationId) => `/vendor/application/${applicationId}/status`,
      sendEmailCode: (applicationId) => `/vendor/application/${applicationId}/send-email-code`,
      verifyEmail: (applicationId) => `/vendor/application/${applicationId}/verify-email`,
      verifyPhone: (applicationId) => `/vendor/application/${applicationId}/verify-phone`,
      checkByToken: (token) => `/vendor/application/token/${token}`,
      check: {
        email: '/vendor/check/email',
        name: '/vendor/check/name'
      }
    }
  },

  brands: {
    list: '/public/brands',
    get: (id) => `/public/brands/${id}`
  },

  cargoCompanies: {
    list: '/public/cargo-companies'
  },

  categories: {
    list: '/public/categories',
    topLevel: '/public/categories/top-level',
    hierarchicalList: '/public/categories/hierarchical',
    search: (searchTerm) => `/public/categories/search/${searchTerm}`,
    attributes: (categoryId) => `/public/categories/${categoryId}/attributes`
  },
  locations: {
    cities: (countryId) => `/public/locations/countries/${countryId}/cities`,
    districts: (cityId) => `/public/locations/cities/${cityId}/districts`
  },
  attributes: {
    list: '/public/attributes',
    search: (searchTerm) => `/public/attributes/search/${searchTerm}`,
    withTerms: (attributeId) => `/public/attributes/${attributeId}/with-terms`
  },
  attributeCoordination: {
    variationAttributes: {
      getWithVendorTerms: (productId) =>
        `/public/attribute-coordination/products/${productId}/variation-attributes`
    },
    specifications: (categoryId, productId) =>
      `/public/attribute-coordination/categories/${categoryId}/products/${productId}/specifications`
  },
  buybox: {
    list: '/public/buybox/products'
  },
  catalog: {
    get: '/public/catalog'
  },
  carousels: {
    list: '/public/carousels',
    items: (id) => `/public/carousels/${id}/items`
  },

  banks: {
    list: '/public/banks'
  }
}

// Auth endpoints
const AUTH_ENDPOINTS = {
  login: '/auth/login',
  register: '/auth/register',
  registerVendor: '/auth/register-vendor',
  logout: '/auth/logout',
  refresh: '/auth/refresh',
  me: '/auth/me',
  remind: '/auth/remind',
  resetPassword: '/auth/reset-password'
}

// Anonymous user endpoints
const ANONYMOUS_ENDPOINTS = {
  cart: {
    show: '/cart',
    create: {
      forVariant: '/cart/variant-product',
      forBasic: '/cart/basic-product'
    },
    delete: (cartItemId) => `/cart/${cartItemId}`,
    update: (cartItemId) => `/cart/${cartItemId}`
  },
  addresses: {
    list: '/addresses',
    save: '/addresses',
    setDefault: '/addresses/default',
    update: (addressId) => `/addresses/${addressId}`,
    delete: (addressId) => `/addresses/${addressId}`,
  }
}

// Protected endpoints (Kimlik doğrulama gerektiren)
const PROTECTED_ENDPOINTS = {
  products: {
    new: '/products/new',
    management: {
      get: (productId) => `/products/management/${productId}`
    },
    save: (productId) => `/products/${productId}`,
    status: (productId) => `/products/${productId}/status`,
    delete: (productId) => `/products/${productId}`,
    stageStatuses: (productId) => `/products/${productId}/stage-statuses`,
    checkSKU: (sku) => `/products/check-sku/${sku}`,
    checkBarcode: (barcode) => `/products/check-barcode/${barcode}`,
    checkModelCode: (modelCode) => `/products/check-model-code/${modelCode}`,

    saveRevision: (productId) => `/products/${productId}/revision`,

    images: {
      save: (productId) => `/products/${productId}/images`,
      saveRevision: (productId) => `/products/${productId}/images/revision`
    },

    meta: {
      create: (productId) => `/products/${productId}/meta`,
      createRevision: (productId) => `/products/${productId}/meta/revision`,
      delete: (productId, featureId) =>
        `/products/${productId}/meta/${featureId}`,
      deleteRevision: (productId, featureId) =>
        `/products/${productId}/meta/${featureId}/revision`
    },

    variations: {
      create: (productId) => `/products/${productId}/variations/create`,
      update: (productId) => `/products/${productId}/variations/update`,
      getVendor: (productId) => `/products/${productId}/variations/vendor`,
      deleteAll: (productId) => `/products/${productId}/variations`
    },

    attributes: {
      save: (productId) => `/products/${productId}/attributes/variants`
    },
    productAttributeTerms: {
      save: (productId) => `/products/${productId}/product-attribute-terms`,
      delete: (productAttributeTermId) =>
        `/products/product-attribute-terms/${productAttributeTermId}`,
      images: {
        update: (productAttributeTermId) =>
          `/products/product-attribute-terms/${productAttributeTermId}/images`
      }
    },
    category: {
      change: (productId, newCategoryId) =>
        `/products/${productId}/change-category/${newCategoryId}`
    }
  },

  admin: {
    settings: {
      get: '/public/admin/site-settings',
      update: '/admin/site-settings',
      logo: '/admin/site-settings/logo'
    },
    categoryFinancial: {
      categories: {
        list: '/admin/category-financial/categories',
        update: '/admin/category-financial/categories',
        bulk: '/admin/category-financial/categories/bulk',
        delete: (categoryId) => `/admin/category-financial/categories/${categoryId}`
      },
      trendyolAdapt: '/admin/category-financial/trendyol/adapt'
    },
    products: {
      approve: (productId) => `/admin/products/${productId}/approve`,
      forceDelete: (productId) => `/admin/products/${productId}/force`
    },
    carousels: {
      list: '/admin/carousels',
      config: '/admin/carousels/config',
      create: '/admin/carousels',
      show: (id) => `/admin/carousels/${id}`,
      update: (id) => `/admin/carousels/${id}`,
      delete: (id) => `/admin/carousels/${id}`
    },
    cargoCompanies: {
      list: '/admin/cargo-companies',
      create: '/admin/cargo-companies',
      show: (id) => `/admin/cargo-companies/${id}`,
      update: (id) => `/admin/cargo-companies/${id}`,
      delete: (id) => `/admin/cargo-companies/${id}`
    },
    shippingProviders: {
      list: '/admin/shipping-providers',
      create: '/admin/shipping-providers',
      show: (id) => `/admin/shipping-providers/${id}`,
      update: (id) => `/admin/shipping-providers/${id}`,
      delete: (id) => `/admin/shipping-providers/${id}`
    },
    vendorApplications: {
      list: '/admin/vendor-applications',
      show: (id) => `/admin/vendor-applications/${id}`,
      update: (id) => `/admin/vendor-applications/${id}`,
      updateStatus: (id) => `/admin/vendor-applications/${id}/status`,
      delete: (id) => `/admin/vendor-applications/${id}`
    },
    regions: {
      cities: '/admin/regions/cities',
      city: (id) => `/admin/regions/cities/${id}`,
      cityDistricts: (cityId) => `/admin/regions/cities/${cityId}/districts`,
      district: (id) => `/admin/regions/districts/${id}`,
      sync: '/admin/regions/sync'
    },
    elasticsearch: {
      reindexProducts: '/admin/elasticsearch/reindex-products',
      reindexVendors: '/admin/elasticsearch/reindex-vendors',
      reindexKeywords: '/admin/elasticsearch/reindex-keywords'
    },
    systemHealth: {
      queue: '/admin/system-health/queue'
    }
  },

  vendor: {
    readiness: '/vendor/readiness',
    products: {
      list: '/vendor/products',
      delete: (productId) => `/vendor/products/${productId}`
    },
    images: {
      list: '/vendor/images',
      save: '/vendor/images'
    },
    orders: {
      list: '/vendor/sub-orders'
    },
    variations: {
      delete: (id) => `/vendor/variations/${id}`
    },
    addresses: {
      list: '/vendor/addresses',
      listByType: (addressType) => `/vendor/addresses/type/${addressType}`,
      getDefault: (addressType) => `/vendor/addresses/default/${addressType}`,
      save: '/vendor/addresses',
      setDefault: '/vendor/addresses/default',
      update: (addressId) => `/vendor/addresses/${addressId}`,
      delete: (addressId) => `/vendor/addresses/${addressId}`,
    },
    representatives: {
      list: '/vendor/representatives',
      create: '/vendor/representatives',
      update: (id) => `/vendor/representatives/${id}`,
      delete: (id) => `/vendor/representatives/${id}`,
      makePrimary: (id) => `/vendor/representatives/${id}/primary`
    },
    settings: {
      store: '/vendor/store-settings',
      update: '/vendor/store-settings',
      company: '/vendor/store-settings/company',
      addresses: '/vendor/store-settings/addresses',
      logo: '/vendor/logo',
      banner: '/vendor/banner'
    },
    payments: {
      statistics: '/vendor/payments/statistics',
      bankAccounts: {
        list: '/vendor/payments/bank-accounts',
        create: '/vendor/payments/bank-accounts',
        update: (id) => `/vendor/payments/bank-accounts/${id}`,
        delete: (id) => `/vendor/payments/bank-accounts/${id}`
      }
    },
    statistics: {
      dashboard: '/vendor/dashboard-statistics'
    }
  },

  orders: {
    list: '/orders',
    create: '/orders',
    get: (orderId) => `/orders/${orderId}`,
    delete: (orderId) => `/orders/${orderId}`,

    sub: {
      get: (subOrderId) => `/orders/sub/${subOrderId}`,
      delete: (subOrderId) => `/orders/sub/${subOrderId}`,
      deleteItem: (itemId) => `/orders/sub/items/${itemId}`,
      saveAddress: '/orders/sub/address',
      saveInput: '/orders/sub/save-input',
      updateStatus: (subOrderId) => `/orders/sub/${subOrderId}/status`
    }
  },

  favorites: {
    list: '/favorites',
    add: '/favorites',
    delete: (productId) => `/favorites/${productId}`,
    check: (productId) => `/favorites/${productId}/check`
  },

  brands: {
    list: '/brands',
    create: '/brands',
    update: (id) => `/brands/${id}`,
    delete: (id) => `/brands/${id}`
  },

  categoryAttributes: {
    create: '/category-attributes',
    update: (categoryId) => `/category-attributes/${categoryId}`,
    delete: (categoryId) => `/category-attributes/${categoryId}`
  },

  attributes: {
    createWithTerms: '/attributes/with-terms',
    updateWithTerms: (attributeId) => `/attributes/${attributeId}/with-terms`,
    delete: (attributeId) => `/attributes/${attributeId}`,
    terms: {
      create: (attributeId) => `/attributes/${attributeId}/terms`,
      delete: (attributeId, termId) =>
        `/attributes/${attributeId}/terms/${termId}`
    }
  },

  attributeCoordination: {
    saveSpecifications: (productId) =>
      `/attribute-coordination/${productId}/specifications`,
    saveSpecificationsRevision: (productId) =>
      `/attribute-coordination/${productId}/specifications/revision`
  },

  revisions: {
    product: {
      get: (productId) => `/revisions/product/${productId}`,
      delete: (revisionId) => `/revisions/${revisionId}`,
      apply: (revisionId) => `/revisions/${revisionId}/apply`,
      reject: (revisionId) => `/revisions/${revisionId}/reject`
    }
  },

  buybox: {
    addVendor: '/buybox/product',
    updateVendor: (productId) => `/buybox/product/${productId}`,
    addVariationVendor: '/buybox/variation'
  },

  shipping: {
    getCode: '/shipping/get-code'
  },

  input: {
    create: '/input',
    update: (id) => `/input/${id}`,
    delete: (id) => `/input/${id}`
  },

  upload: '/upload'
}

// Ana API endpoint yapısı
export const API_ENDPOINTS = {
  public: PUBLIC_ENDPOINTS,
  auth: AUTH_ENDPOINTS,
  anonymous: ANONYMOUS_ENDPOINTS,
  protected: PROTECTED_ENDPOINTS
}

// Local BFF endpoints exposed by Nitro server (Nuxt)
export const BFF_ENDPOINTS = {
  siteMeta: '/api/site-meta',
  auth: {
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    refresh: '/api/auth/refresh',
    me: '/api/auth/me',
    register: '/api/auth/register',
    registerVendor: '/api/auth/register-vendor',
    remind: '/api/auth/remind',
    resetPassword: '/api/auth/reset-password'
  }
}

// Composable fonksiyonu
export const useApiEndpoints = () => {
  return {
    BASE_URL,
    API_ENDPOINTS,
    BFF_ENDPOINTS
  }
}
